import React, { useState, useEffect, useMemo } from 'react';
import MarketTable from '../components/market-table';
import SearchBar from '../components/search-bar';
import StatCard from '../components/stat-card';
import Loader from '../components/loader';
import ErrorBanner from '../components/error-banner';
import { getWatchlist, subscribeToWatchlist } from '../services/watchlist-service';
import { fetchTopCoins } from '../services/coingecko-api';
import { fetchAllBinanceTickers, fetchBinanceKlinesBatch } from '../services/binance-api';
import { isStablecoin, buildSymbolIndex } from '../utils/symbol-mapper';
import { getMarketStatistics } from '../services/insights-service';
import {
  calculateRSI,
  calculateMACD,
  calculateStochastic
} from '../utils/technical-indicators';
import { calculateTrend } from '../utils/trend-calculator';
import { parseBinanceKlines, extractPrices } from '../utils/timeframe-utils';
import { useAuthInit } from '../hooks/use-auth-init';
import './watchlist-page.css';

function WatchlistPage() {
  const [watchlistCoins, setWatchlistCoins] = useState([]);
  const [watchlistIds, setWatchlistIds] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const { isReady: authReady } = useAuthInit();

  // ─── Subscribe to watchlist changes ──────────────────────────────────────────
  useEffect(() => {
    if (!authReady) return;
    console.log('[Watchlist] Setting up subscription...');
    const unsubscribe = subscribeToWatchlist((coinIds) => {
      console.log('[Watchlist] Updated:', coinIds);
      setWatchlistIds(coinIds);
    });
    return unsubscribe;
  }, [authReady]);

  // ─── Re-fetch when watchlist changes ─────────────────────────────────────────
  useEffect(() => {
    if (watchlistIds.length === 0) {
      setWatchlistCoins([]);
      setIsLoading(false);
      return;
    }
    fetchWatchlistData();
  }, [watchlistIds]);

  // ─── Technical indicator calculator (same as dashboard) ──────────────────────
  const calculateTimeframeIndicators = (priceData) => {
    if (!priceData || !priceData.closes || priceData.closes.length < 30) {
      return { rsi: null, macd: null, stochastic: null, trend: 'N/A' };
    }

    const rsi        = calculateRSI(priceData.closes, 7);
    const macd       = calculateMACD(priceData.closes, 12, 26, 9);
    const stochastic = calculateStochastic(
      priceData.highs, priceData.lows, priceData.closes, 14, 3
    );

    return {
      rsi,
      macd,
      stochastic,
      trend: calculateTrend(
        rsi[rsi.length - 1],
        macd.histogram[macd.histogram.length - 1],
        stochastic.k[stochastic.k.length - 1],
        stochastic.d[stochastic.d.length - 1]
      ),
    };
  };

  // ─── Main data fetch ──────────────────────────────────────────────────────────
  const fetchWatchlistData = async () => {
    try {
      setIsLoading(true);
      setError(null);

      console.log('[Watchlist] Fetching data for:', watchlistIds);

      // CoinGecko: fetch top 250 to find watchlisted coins (metadata + market caps)
      const allCoins = await fetchTopCoins(250);
      const watchlistedCoins = allCoins.filter(coin => watchlistIds.includes(coin.id));

      if (watchlistedCoins.length === 0) {
        setWatchlistCoins([]);
        setIsLoading(false);
        return;
      }

      console.log(`[Watchlist] Found ${watchlistedCoins.length} coins. Fetching Binance data...`);

      // Build symbol map for watchlisted coins only
      const symbolIndex   = buildSymbolIndex(watchlistedCoins);
      const binanceSymbols = symbolIndex.map(e => e.binanceSymbol);

      // Binance: parallel fetch of tickers + all three kline timeframes
      const [tickerMap, fourHourRaw, dailyRaw, weeklyRaw] = await Promise.all([
        fetchAllBinanceTickers(binanceSymbols),
        fetchBinanceKlinesBatch(
          symbolIndex.map(({ binanceSymbol }) => ({ symbol: binanceSymbol, interval: '4h', limit: 200 }))
        ),
        fetchBinanceKlinesBatch(
          symbolIndex.map(({ binanceSymbol }) => ({ symbol: binanceSymbol, interval: '1d', limit: 90 }))
        ),
        fetchBinanceKlinesBatch(
          symbolIndex.map(({ binanceSymbol }) => ({ symbol: binanceSymbol, interval: '1w', limit: 52 }))
        ),
      ]);

      // Build quick lookup
      const symbolLookup = {};
      symbolIndex.forEach(({ coinGeckoId, binanceSymbol }) => {
        symbolLookup[coinGeckoId] = binanceSymbol;
      });

      // Process each watchlisted coin
      const processedCoins = watchlistedCoins.map(coin => {
        const binanceSym = symbolLookup[coin.id];
        const ticker     = binanceSym ? tickerMap[binanceSym] : null;

        const mergedCoin = {
          ...coin,
          current_price:               ticker ? ticker.lastPrice          : coin.current_price,
          price_change_percentage_24h: ticker ? ticker.priceChangePercent : coin.price_change_percentage_24h,
          total_volume:                coin.total_volume,
        };

        if (isStablecoin(coin.id)) {
          return {
            ...mergedCoin,
            trends:        { weekly: 'Stable', daily: 'Stable', fourHour: 'Stable' },
            indicators:    null,
            historicalData: null,
          };
        }

        if (!binanceSym) {
          return {
            ...mergedCoin,
            trends:        { weekly: 'N/A', daily: 'N/A', fourHour: 'N/A' },
            indicators:    null,
            historicalData: null,
          };
        }

        try {
          const fourHourCandles = parseBinanceKlines(fourHourRaw[binanceSym]);
          const dailyCandles    = parseBinanceKlines(dailyRaw[binanceSym]);
          const weeklyCandles   = parseBinanceKlines(weeklyRaw[binanceSym]);

          const fourHourIndicators = calculateTimeframeIndicators(extractPrices(fourHourCandles));
          const dailyIndicators    = calculateTimeframeIndicators(extractPrices(dailyCandles));
          const weeklyIndicators   = calculateTimeframeIndicators(extractPrices(weeklyCandles));

          return {
            ...mergedCoin,
            trends: {
              weekly:   weeklyIndicators.trend,
              daily:    dailyIndicators.trend,
              fourHour: fourHourIndicators.trend,
            },
            indicators: {
              weekly:   weeklyIndicators,
              daily:    dailyIndicators,
              fourHour: fourHourIndicators,
            },
            historicalData: {
              prices:   fourHourCandles.slice(-60).map(c => [c[0], c[4]]),
              fourHour: fourHourCandles,
              daily:    dailyCandles,
              weekly:   weeklyCandles,
            },
            binanceSymbol: binanceSym,
          };
        } catch (err) {
          console.error(`[Watchlist] Error processing ${coin.id}:`, err);
          return {
            ...mergedCoin,
            trends:        { weekly: 'N/A', daily: 'N/A', fourHour: 'N/A' },
            indicators:    null,
            historicalData: null,
          };
        }
      });

      // Preserve original watchlist order
      const sortedCoins = processedCoins.sort((a, b) =>
        watchlistIds.indexOf(a.id) - watchlistIds.indexOf(b.id)
      );

      console.log('[Watchlist] Done. Processed:', sortedCoins.length, 'coins');
      setWatchlistCoins(sortedCoins);

    } catch (err) {
      console.error('[Watchlist] Error fetching data:', err);
      setError(`Failed to load watchlist: ${err.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  // ─── Derived stats ────────────────────────────────────────────────────────────
  const watchlistStats = useMemo(() => getMarketStatistics(watchlistCoins), [watchlistCoins]);

  const handleSearch = (term) => setSearchTerm(term);
  const handleRetry  = () => fetchWatchlistData();

  // ─── Render ───────────────────────────────────────────────────────────────────
  if (!authReady) {
    return (
      <div className="watchlist-page">
        <div className="watchlist-loading">
          <Loader size="large" text="Initializing..." />
        </div>
      </div>
    );
  }

  if (isLoading && watchlistCoins.length === 0) {
    return (
      <div className="watchlist-page">
        <div className="watchlist-loading">
          <Loader size="large" text="Loading your watchlist..." />
        </div>
      </div>
    );
  }

  if (!isLoading && watchlistIds.length === 0) {
    return (
      <div className="watchlist-page">
        <div className="page-header">
          <h1>Your Watchlist</h1>
          <p className="page-subtitle">Track your favorite cryptocurrencies</p>
        </div>
        <div className="empty-watchlist">
          <div className="empty-icon">⭐</div>
          <h2>Your watchlist is empty</h2>
          <p>
            Start building your watchlist by clicking the star icon next to any
            cryptocurrency on the dashboard.
          </p>
          <button className="btn-primary" onClick={() => window.location.href = '/'}>
            Go to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="watchlist-page">
      {error && (
        <ErrorBanner
          message={error}
          type="error"
          onDismiss={() => setError(null)}
          onRetry={handleRetry}
          className="watchlist-error"
        />
      )}

      <div className="page-header">
        <h1>Your Watchlist</h1>
        <p className="page-subtitle">
          {watchlistCoins.length} cryptocurrencies in your watchlist
        </p>
      </div>

      <div className="watchlist-stats-grid">
        <StatCard label="Total Coins"      value={watchlistStats.totalCoins}                       type="number" icon="📊" isLoading={isLoading} />
        <StatCard label="Avg Change"       value={`${watchlistStats.averageChange?.toFixed(2)}%`}  change={watchlistStats.averageChange} icon="📈" isLoading={isLoading} />
        <StatCard label="Bullish Signals"  value={watchlistStats.bullishCount}                     icon="🚀"     className={watchlistStats.bullishCount > watchlistStats.bearishCount ? 'highlight' : ''} isLoading={isLoading} />
        <StatCard label="Bearish Signals"  value={watchlistStats.bearishCount}                     icon="📉"     className={watchlistStats.bearishCount > watchlistStats.bullishCount ? 'warning'   : ''} isLoading={isLoading} />
      </div>

      <SearchBar onSearch={handleSearch} placeholder="Search your watchlist..." disabled={isLoading} />

      <MarketTable
        data={watchlistCoins}
        searchTerm={searchTerm}
        isLoading={isLoading && watchlistCoins.length === 0}
      />
    </div>
  );
}

export default WatchlistPage;
